// src/pages/Appli/AppliAdmin/ValidationBadgesUsers.jsx
import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";

import { getClubs } from "../../../services/clubServices";
import {
  getAllUsersWithValidationBadges,
  toAbsoluteBadgeUrl,
} from "../../../services/validationBadgeServices";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

import {
  FaDownload,
  FaSearch,
  FaUsers,
  FaIdBadge,
  FaBuilding,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

/* ================= Helpers ================= */
const BADGE_THUMB = "/img/luttecontrelesderives.png";
const PAGE_SIZE = 30;

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

function toCsv(rows, sep = ";") {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (s.includes('"') || s.includes(sep) || /\r|\n/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  return rows.map((r) => r.map(esc).join(sep)).join("\r\n");
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function formatDateFR(date) {
  if (!date) return "Date inconnue";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "Date inconnue";
  return d.toLocaleDateString("fr-FR");
}

function sortByDateDesc(arr = []) {
  return [...arr].sort((a, b) => {
    const ta = a?.validation_date ? new Date(a.validation_date).getTime() : 0;
    const tb = b?.validation_date ? new Date(b.validation_date).getTime() : 0;
    return tb - ta;
  });
}

function normalizeUserRow(u) {
  const clubs = Array.isArray(u?.clubs)
    ? u.clubs.map((c) => ({
        id: c?.id,
        name: c?.name || "",
        departement: c?.departement || "",
      }))
    : [];

  const primaryRoles = Array.isArray(u?.primaryRoles)
    ? u.primaryRoles.filter(Boolean)
    : u?.primaryRoles
    ? [u.primaryRoles]
    : [];

  const rawBadges = Array.isArray(u?.badges)
    ? u.badges
    : u?.badge
    ? [u.badge]
    : [];

  const badges = sortByDateDesc(
    rawBadges
      .filter(Boolean)
      .map((b, index) => ({
        id: b?.id || `${u?.id || "user"}-badge-${index}`,
        badge_url: b?.badge_url || "",
        validation_date: b?.validation_date || b?.createdAt || null,
      }))
  );

  const departements = [...new Set(clubs.map((c) => c.departement).filter(Boolean))];

  return {
    id: u?.id,
    prenom: u?.prenom || "",
    nom: u?.nom || "",
    email: u?.email || "",
    photoURL: u?.photoURL || "",
    clubs,
    primaryRoles,
    badges,
    departements,
    lastValidationDate: badges[0]?.validation_date || null,
  };
}

/* ================= UI ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div
      className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({ totalUsers, totalBadges, totalDeps }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Validation des badges de formation
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Utilisateurs ayant déposé une ou plusieurs attestations de formation
              contre les violences sexuelles.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[620px]">
            <CompactStat
              icon={<FaUsers />}
              label="Utilisateurs"
              value={totalUsers}
              accent="indigo"
            />
            <CompactStat
              icon={<FaIdBadge />}
              label="Badges"
              value={totalBadges}
              accent="emerald"
            />
            <CompactStat
              icon={<FaBuilding />}
              label="Comités"
              value={totalDeps}
              accent="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function RolePill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
      {children}
    </span>
  );
}

function DeptPill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
      {children}
    </span>
  );
}

function StructurePill({ club }) {
  return (
    <div className="inline-flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
      <span className="max-w-[260px] truncate font-medium">
        {club?.name || "Club sans nom"}
      </span>
      {club?.departement ? (
        <span className="inline-flex shrink-0 items-center rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
          {club.departement}
        </span>
      ) : null}
    </div>
  );
}

function FiltersBar({
  searchTerm,
  setSearchTerm,
  departementsOptions,
  selectedDepartement,
  setSelectedDepartement,
  roleOptions,
  selectedRole,
  setSelectedRole,
  onReset,
  onExport,
  loading,
  totalCount,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),220px,220px,auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Nom, email, club, comité..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <select
          value={selectedDepartement}
          onChange={(e) => setSelectedDepartement(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="">Tous les comités</option>
          {departementsOptions.map((dep) => (
            <option key={dep} value={dep}>
              {dep}
            </option>
          ))}
        </select>

        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          <option value="">Tous les rôles</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onExport}
            disabled={loading || totalCount === 0}
            className="inline-flex h-11 items-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <FaDownload />
            Export CSV
          </button>
        </div>
      </div>
    </div>
  );
}

function BadgeThumb({ badge, index, small = false }) {
  const url = badge?.badge_url ? toAbsoluteBadgeUrl(badge.badge_url) : null;

  const thumb = (
    <div
      className="group shrink-0"
      title={`Badge #${index + 1} — ${formatDateFR(badge?.validation_date)}`}
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white transition group-hover:-translate-y-0.5 group-hover:shadow-md dark:border-slate-700 dark:bg-slate-900">
        <img
          src={BADGE_THUMB}
          alt="Badge numérique"
          className={small ? "h-11 w-11 object-cover" : "h-12 w-12 object-cover"}
        />
      </div>
      <div className="mt-1 w-full text-center text-[10px] text-slate-500 dark:text-slate-400">
        {formatDateFR(badge?.validation_date)}
      </div>
    </div>
  );

  if (!url) return thumb;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block">
      {thumb}
    </a>
  );
}

function UserPhoto({ src, alt }) {
  const [imgSrc, setImgSrc] = useState(
    toAbsoluteUserPhotoUrl(src) || "/img/user_avatar.jpg"
  );

  useEffect(() => {
    setImgSrc(toAbsoluteUserPhotoUrl(src) || "/img/user_avatar.jpg");
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc("/img/user_avatar.jpg")}
      className="h-10 w-10 rounded-xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function StructuresCompact({ user, max = 2 }) {
  const preview = user.clubs.slice(0, max);
  const hidden = Math.max(0, user.clubs.length - preview.length);

  return (
    <div className="space-y-2">
      {user.primaryRoles.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {user.primaryRoles.map((role) => (
            <RolePill key={`${user.id}-${role}`}>{role}</RolePill>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-400">Aucun rôle renseigné</div>
      )}

      {user.clubs.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {preview.map((club) => (
            <StructurePill
              key={`${user.id}-${club.id || club.name}`}
              club={club}
            />
          ))}

          {hidden > 0 && (
            <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              + {hidden} autre{hidden > 1 ? "s" : ""}
            </span>
          )}
        </div>
      ) : (
        <div className="text-xs text-slate-400">Aucun club rattaché</div>
      )}

      {user.departements.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {user.departements.map((dep) => (
            <DeptPill key={`${user.id}-dep-${dep}`}>Comité {dep}</DeptPill>
          ))}
        </div>
      )}
    </div>
  );
}

function BadgesCompact({ user, small = false }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {user.badges.length} badge{user.badges.length > 1 ? "s" : ""}
        </span>

        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          Dernière validation : {formatDateFR(user.lastValidationDate)}
        </span>
      </div>

      {user.badges.length > 0 ? (
        <div className="overflow-x-auto pb-1">
          <div className="flex w-max gap-2">
            {user.badges.map((badge, index) => (
              <BadgeThumb
                key={badge.id || `${user.id}-badge-${index}`}
                badge={badge}
                index={index}
                small={small}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-400">Aucun badge disponible</div>
      )}
    </div>
  );
}

function UsersTableDesktop({ rows, loading }) {
  return (
    <div className="hidden lg:block">
      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[1050px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[26%] px-4 py-3 font-semibold">Utilisateur</th>
                <th className="w-[38%] px-4 py-3 font-semibold">Rôles / Structure</th>
                <th className="w-[36%] px-4 py-3 font-semibold">Badges déposés</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                    Chargement…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-slate-500">
                    Aucun résultat avec ces filtres.
                  </td>
                </tr>
              ) : (
                rows.map((u) => (
                  <tr
                    key={u.id}
                    className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-start gap-3">
                        <UserPhoto src={u.photoURL} alt={`${u.nom} ${u.prenom}`} />

                        <div className="min-w-0">
                          <div className="break-words text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                             {u.nom} {u.prenom}
                          </div>

                          {u.email ? (
                            <a
                              href={`mailto:${u.email}`}
                              className="mt-1 block break-all text-[12px] text-indigo-600 hover:underline dark:text-indigo-300"
                            >
                              {u.email}
                            </a>
                          ) : (
                            <div className="mt-1 text-[12px] text-slate-400">
                              Email non renseigné
                            </div>
                          )}

                          <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                            Validé le{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-200">
                              {formatDateFR(u.lastValidationDate)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <StructuresCompact user={u} max={2} />
                    </td>

                    <td className="px-4 py-3.5">
                      <BadgesCompact user={u} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UsersCardsMobile({ rows, loading }) {
  return (
    <div className="lg:hidden">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Aucun résultat avec ces filtres.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((u) => (
            <div
              key={u.id}
              className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <UserPhoto src={u.photoURL} alt={`${u.nom} ${u.prenom}`} />

                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                     {u.nom} {u.prenom}
                  </div>

                  {u.email ? (
                    <a
                      href={`mailto:${u.email}`}
                      className="mt-1 block break-all text-[12px] text-indigo-600 hover:underline dark:text-indigo-300"
                    >
                      {u.email}
                    </a>
                  ) : (
                    <div className="mt-1 text-[12px] text-slate-400">
                      Email non renseigné
                    </div>
                  )}

                  <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    Dernière validation :{" "}
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      {formatDateFR(u.lastValidationDate)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Rôles / Structure
                </div>
                <StructuresCompact user={u} max={2} />
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Badges déposés
                </div>
                <BadgesCompact user={u} small />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page <span className="font-semibold text-slate-900 dark:text-white">{page}</span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page === pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
const ValidationBadgesUsers = () => {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState([]);
  const [departementsOptions, setDepartementsOptions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartement, setSelectedDepartement] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  const debouncedSearch = useDebounced(searchTerm, 300);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const [usersRes, clubsRes] = await Promise.all([
          getAllUsersWithValidationBadges(),
          getClubs(),
        ]);

        const normalizedUsers = (Array.isArray(usersRes) ? usersRes : [])
          .map(normalizeUserRow)
          .sort((a, b) =>
            `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, "fr", {
              sensitivity: "base",
            })
          );

        const deps = Array.from(
          new Set(
            (Array.isArray(clubsRes) ? clubsRes : [])
              .map((c) => c?.departement_club)
              .filter(Boolean)
          )
        ).sort((a, b) => String(a).localeCompare(String(b), "fr"));

        setAllRows(normalizedUsers);
        setDepartementsOptions(deps);
      } catch (e) {
        console.error(e);
        addNotification(
          "Erreur lors du chargement des utilisateurs avec badge",
          "error"
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [addNotification]);

  const roleOptions = useMemo(() => {
    return Array.from(
      new Set(allRows.flatMap((u) => u.primaryRoles).filter(Boolean))
    ).sort((a, b) => String(a).localeCompare(String(b), "fr"));
  }, [allRows]);

  const filteredRows = useMemo(() => {
    const nq = normalize(debouncedSearch);

    return allRows.filter((u) => {
      const matchesSearch =
        !nq ||
        normalize(
          [
            u.nom,
            u.prenom,
            u.email,
            ...u.primaryRoles,
            ...u.departements,
            ...u.clubs.map((c) => c.name),
          ].join(" ")
        ).includes(nq);

      const matchesDep =
        !selectedDepartement ||
        u.departements.some((d) => String(d) === String(selectedDepartement));

      const matchesRole =
        !selectedRole ||
        u.primaryRoles.some((r) => normalize(r) === normalize(selectedRole));

      return matchesSearch && matchesDep && matchesRole;
    });
  }, [allRows, debouncedSearch, selectedDepartement, selectedRole]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedDepartement, selectedRole]);

  const totalUsers = filteredRows.length;

  const totalBadges = useMemo(
    () => filteredRows.reduce((sum, u) => sum + u.badges.length, 0),
    [filteredRows]
  );

  const totalDeps = useMemo(
    () => new Set(filteredRows.flatMap((u) => u.departements)).size,
    [filteredRows]
  );

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  const paginatedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, safePage]);

  const handleExport = () => {
    try {
      if (filteredRows.length === 0) return;

      const header = [
        "Nom",
        "Prénom",
        "Email",
        "Rôles principaux",
        "URL Photo Profil",
        "Clubs (Nom [Département])",
        "Comités (Départements)",
        "Nombre de badges",
        "URLs Badges",
        "Dates de validation",
      ];

      const data = filteredRows.map((u) => {
        const clubsStr = (u.clubs || [])
          .map((c) => `${c.name} [${c.departement || "?"}]`)
          .join(" | ");

        const depsStr = (u.departements || []).join(" | ");

        const badgesUrls = (u.badges || [])
          .map((b) => (b?.badge_url ? toAbsoluteBadgeUrl(b.badge_url) : ""))
          .filter(Boolean)
          .join(" | ");

        const badgesDates = (u.badges || [])
          .map((b) => formatDateFR(b?.validation_date))
          .join(" | ");

        return [
          u.nom || "",
          u.prenom || "",
          u.email || "",
          (u.primaryRoles || []).join(" | "),
          u.photoURL ? toAbsoluteUserPhotoUrl(u.photoURL) : "",
          clubsStr,
          depsStr,
          u.badges.length,
          badgesUrls,
          badgesDates,
        ];
      });

      const csv = toCsv([header, ...data]);
      downloadCsv(
        `utilisateurs_badges_violences_sexuelles_${new Date()
          .toISOString()
          .slice(0, 10)}.csv`,
        csv
      );
    } catch (e) {
      console.error(e);
      addNotification("Erreur lors de l'export CSV", "error");
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedDepartement("");
    setSelectedRole("");
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          totalUsers={totalUsers}
          totalBadges={totalBadges}
          totalDeps={totalDeps}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            departementsOptions={departementsOptions}
            selectedDepartement={selectedDepartement}
            setSelectedDepartement={setSelectedDepartement}
            roleOptions={roleOptions}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            onReset={handleReset}
            onExport={handleExport}
            loading={loading}
            totalCount={totalUsers}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {totalUsers} utilisateur{totalUsers > 1 ? "s" : ""}
          </span>

          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {totalBadges} badge{totalBadges > 1 ? "s" : ""}
          </span>

          {selectedDepartement && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Comité {selectedDepartement}
            </span>
          )}

          {selectedRole && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Rôle : {selectedRole}
            </span>
          )}

          {!loading && totalUsers > 0 && (
            <span className="ml-auto text-xs">
              Affichage {paginatedRows.length} • page {safePage} / {pageCount}
            </span>
          )}
        </div>

        <div className="mt-5">
          <UsersTableDesktop rows={paginatedRows} loading={loading} />
          <UsersCardsMobile rows={paginatedRows} loading={loading} />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={pageCount}
          setPage={setPage}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ValidationBadgesUsers;